<script setup>
import {onMounted, nextTick, ref, computed, watch} from 'vue';
import {useRouter} from 'vue-router';
import ServiceRequirementService from '@/services/ServiceRequirementService.js';
import {formatDate, getCssVar, getContrastColor} from '@/helper';
import {getLoggedUser} from '@/services/AuthService';
import {useI18n} from 'vue-i18n';
import Button from '@/components/forms/Button.vue';

const {t} = useI18n();
const router = useRouter();

const unresolved = ref([]);
const resolved = ref([]);
const isLoading = ref(true);
// IMA-511: klient ve stavu Inheritance_process/Inheritance_closed/Invalid má jen náhled -
// tlačítko by jinak odeslalo POST, který backend zamítne (403), bez jakékoliv hlášky.
const isReadOnly = ref(false);
getLoggedUser().then(user => {
    isReadOnly.value = !!user?.client?.is_read_only;
});

const STORAGE_KEY = 'CLIENT-SERVICE-REQUIREMENTS-TAB';

/** Výchozí tab se načte z localStorage, fallback na aktivní požadavky. */
const activeTab = ref(localStorage.getItem(STORAGE_KEY) ?? 'active');

watch(activeTab, val => localStorage.setItem(STORAGE_KEY, val));

/** Zkrátí text požadavku na max. 120 znaků s výpustkou. */
function truncateText(text, max = 120) {
    if (!text) return '';
    return text.length > max ? text.slice(0, max) + '…' : text;
}

/**
 * FilterFn pro date sloupce v local mode — datepicker dává "YYYY-MM-DD",
 * ISO datetime z API začíná stejným formátem → porovnáme substring.
 */
function datepickerFilterFn(data, filterString) {
    if (!filterString) return true;
    return data ? String(data).startsWith(filterString) : false;
}

/** Sloupce pro tabulku otevřených požadavků. */
const unresolvedColumns = ref([
    {
        label: t('clientServiceRequirements.col_text'),
        field: 'text',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('clientServiceRequirements.col_status'),
        field: 'status',
        sortable: true,
        filterOptions: {enabled: false},
    },
    {
        label: t('clientServiceRequirements.col_created_at'),
        field: 'created_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: '',
        field: 'action',
        sortable: false,
        width: '60px',
    },
]);

/** Sloupce pro tabulku vyřešených požadavků — přidá datum vyřešení. */
const resolvedColumns = ref([
    {
        label: t('clientServiceRequirements.col_text'),
        field: 'text',
        sortable: false,
        filterOptions: {enabled: false},
    },
    {
        label: t('clientServiceRequirements.col_status'),
        field: 'status',
        sortable: true,
        filterOptions: {enabled: false},
    },
    {
        label: t('clientServiceRequirements.col_resolved_at'),
        field: 'resolved_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: t('clientServiceRequirements.col_created_at'),
        field: 'created_at',
        sortable: true,
        filterOptions: {enabled: true, datePicker: true, filterFn: datepickerFilterFn},
    },
    {
        label: '',
        field: 'action',
        sortable: false,
        width: '60px',
    },
]);

const tabGroupRef = ref(null);

onMounted(() => {
    fetchRequirements();
    nextTick(() => {
        const btns = tabGroupRef.value?.querySelectorAll('button');
        if (btns?.length) {
            const maxW = Math.max(...[...btns].map(b => b.scrollWidth));
            btns.forEach(b => (b.style.minWidth = maxW + 'px'));
        }
    });
});

function fetchRequirements() {
    isLoading.value = true;
    ServiceRequirementService.getMyRequirements().then(data => {
        unresolved.value = data.unresolved ?? [];
        resolved.value = data.resolved ?? [];
    }).finally(() => {
        isLoading.value = false;
    });
}

/** Přeloží hodnotu statusu do čitelného textu. */
function statusLabel(status) {
    return t('clientServiceRequirements.status_' + status, status);
}

/** Vrací inline styl pro badge statusu — barva textu je vypočtena dle WCAG kontrastu. */
function statusBadgeStyle(status) {
    const bg1 = getCssVar('--primaryColor', '#004148');
    const bg2 = getCssVar('--primaryHoverColor', '#046e7a');
    const primary = { color: getContrastColor(bg1), backgroundColor: bg1, borderColor: bg1 };
    const hover   = { color: getContrastColor(bg2), backgroundColor: bg2, borderColor: bg2 };
    const light   = { color: bg1, backgroundColor: '#f0f2f4', borderColor: '#f0f2f4' };
    const map = {
        NEW: light,
        IN_PROGRESS: primary,
        WAITING_FOR_CLIENT: hover,
        WAITING_FOR_BO: light,
        CLOSED: primary,
    };
    return map[status] ?? light;
}

function goToDetail(id) {
    router.push({name: 'ClientServiceRequirementDetail', params: {id}});
}

function goToCreate() {
    router.push({name: 'ServiceRequirementsCreate'});
}
</script>

<template>
    <div class="page-section">
        <div class="page-section__heading">
            <div class="row">
                <div class="col-3">
                    <a @click="router.back()" class="page-section__heading__back">
                        <i class="mdi mdi-arrow-left"></i> {{ t('clientServiceRequirements.back') }}
                    </a>
                </div>
                <div class="col-6 text-center">
                    <div class="page-section__title">{{ t('clientServiceRequirements.title') }}</div>
                </div>
                <div class="col-3 d-flex justify-content-end align-items-center">
                    <Button v-if="!isReadOnly" :label="t('clientServiceRequirements.new_request')" icon="mdi-plus" @click="goToCreate"/>
                </div>
            </div>
        </div>
    </div>

    <div class="page-section__content">
        <div v-if="isLoading" class="text-center py-5">
            <span class="mdi mdi-loading mdi-spin me-2"></span>{{ t('clientServiceRequirements.loading') }}
        </div>

        <template v-else>
            <!-- Tab přepínač -->
            <div ref="tabGroupRef" class="d-flex gap-2 mb-4">
                <button
                    class="btn btn-normal"
                    :class="activeTab === 'active' ? 'btn-primary' : 'btn-outline-tenant'"
                    @click="activeTab = 'active'"
                >
                    {{ t('clientServiceRequirements.tab_active') }}
                </button>
                <button
                    class="btn btn-normal"
                    :class="activeTab === 'resolved' ? 'btn-primary' : 'btn-outline-tenant'"
                    @click="activeTab = 'resolved'"
                >
                    {{ t('clientServiceRequirements.tab_resolved') }}
                </button>
            </div>

            <!-- Tabulka aktivních požadavků -->
            <vue-good-table
                v-if="activeTab === 'active'"
                :columns="unresolvedColumns"
                :rows="unresolved"
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    rowsPerPageLabel: t('clientServiceRequirements.entriesPerPage'),
                    nextLabel: t('clientServiceRequirements.next'),
                    prevLabel: t('clientServiceRequirements.previous'),
                    ofLabel: 'z',
                }"
            >
                <template #column-filter="{ column, updateFilters }">
                    <input
                        v-if="column.filterOptions?.datePicker"
                        type="date"
                        class="vgt-input"
                        @change="updateFilters(column, $event.target.value)"
                    />
                </template>
                <template #table-row="props">
                    <!-- Náhled textu požadavku — max 120 znaků s výpustkou -->
                    <span v-if="props.column.field === 'text'" class="text-muted small">
                        {{ truncateText(props.row.text) }}
                    </span>
                    <!-- Status badge -->
                    <span v-else-if="props.column.field === 'status'">
                        <span class="badge" :style="statusBadgeStyle(props.row.status)">
                            {{ statusLabel(props.row.status) }}
                        </span>
                    </span>
                    <!-- Formátované datum vytvoření -->
                    <span v-else-if="props.column.field === 'created_at'">
                        {{ formatDate(props.row.created_at, 'd.m.Y H:i') }}
                    </span>
                    <!-- Akční tlačítko pro přechod na detail -->
                    <span v-else-if="props.column.field === 'action'" style="float: right">
                        <button type="button" class="btn btn-link btn-sm" :title="t('clientServiceRequirements.detail')" @click="goToDetail(props.row.id)">
                            <i class="mdi mdi-eye"/>
                        </button>
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <template v-slot:emptystate>
                    <div class="text-center">{{ t('clientServiceRequirements.no_unresolved') }}</div>
                </template>
            </vue-good-table>

            <!-- Tabulka vyřešených požadavků -->
            <vue-good-table
                v-else
                :columns="resolvedColumns"
                :rows="resolved"
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    rowsPerPageLabel: t('clientServiceRequirements.entriesPerPage'),
                    nextLabel: t('clientServiceRequirements.next'),
                    prevLabel: t('clientServiceRequirements.previous'),
                    ofLabel: 'z',
                }"
            >
                <template #column-filter="{ column, updateFilters }">
                    <input
                        v-if="column.filterOptions?.datePicker"
                        type="date"
                        class="vgt-input"
                        @change="updateFilters(column, $event.target.value)"
                    />
                </template>
                <template #table-row="props">
                    <!-- Náhled textu požadavku — max 120 znaků s výpustkou -->
                    <span v-if="props.column.field === 'text'" class="text-muted small">
                        {{ truncateText(props.row.text) }}
                    </span>
                    <!-- Status badge -->
                    <span v-else-if="props.column.field === 'status'">
                        <span class="badge" :style="statusBadgeStyle(props.row.status)">
                            {{ statusLabel(props.row.status) }}
                        </span>
                    </span>
                    <!-- Datum vyřešení -->
                    <span v-else-if="props.column.field === 'resolved_at'">
                        {{ props.row.resolved_at ? formatDate(props.row.resolved_at, 'd.m.Y H:i') : '---' }}
                    </span>
                    <!-- Formátované datum vytvoření -->
                    <span v-else-if="props.column.field === 'created_at'">
                        {{ formatDate(props.row.created_at, 'd.m.Y H:i') }}
                    </span>
                    <!-- Akční tlačítko pro přechod na detail -->
                    <span v-else-if="props.column.field === 'action'" style="float: right">
                        <button type="button" class="btn btn-link btn-sm" :title="t('clientServiceRequirements.detail')" @click="goToDetail(props.row.id)">
                            <i class="mdi mdi-eye"/>
                        </button>
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <template v-slot:emptystate>
                    <div class="text-center">{{ t('clientServiceRequirements.no_resolved') }}</div>
                </template>
            </vue-good-table>
        </template>
    </div>
</template>
